/**
 * @file Сервис трансляции технических названий в читаемые человеком
 * @module membrane/src/services/sphereNameTranslator
 * 
 * Транслирует технические ID сфер и relation kinds в читаемые названия.
 * Подготовлено для будущей локализации (многоязычность).
 */

// Кэш для хранения названий сфер (ID → читаемое название)
const sphereNameCache = new Map<string, string>();

const isStatic = import.meta.env.VITE_MIRROR_STATIC === 'true';
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';
const MIRROR_BASE = isStatic ? '' : API_BASE_URL;
const API_MIRROR = isStatic ? '/mirror' : `${MIRROR_BASE}/api/mirror`;

// Словарь для relation kinds (техническое название → читаемое)
const relationKindTranslations: Record<string, Record<string, string>> = {
  ru: {
    'communicates_via': 'Общается через',
    'supports': 'Поддерживает',
    'requires': 'Требует',
    'enables': 'Делает возможным',
    'is_expressed_in': 'Проявляется в',
    'holds_threshold': 'Удерживает порог',
    'permeates': 'Пронизывает',
    'manifests_through': 'Проявляется через',
    'tensions_with': 'В напряжении с',
    'orients': 'Направляет',
    'protects': 'Защищает',
    'filters': 'Фильтрует',
    'is_felt_in': 'Ощущается в',
    'preconditions': 'Предусловие для',
    'manifests_loss_of': 'Проявляет утрату',
    'foundation_for': 'Основание для',
    'crystallizes_into': 'Кристаллизуется в',
    'emerges_within': 'Возникает в',
    'bridges_to': 'Мостит к',
    'provides_space_for': 'Даёт место для',
    'hosts': 'Принимает',
    'is_named_through': 'Именуется через',
    'provides_evidence_for': 'Даёт основание для',
    'tunes': 'Настраивает',
  },
  en: {
    'communicates_via': 'Communicates via',
    'supports': 'Supports',
    'requires': 'Requires',
    'enables': 'Enables',
    'is_expressed_in': 'Expressed in',
    'holds_threshold': 'Holds threshold',
    'permeates': 'Permeates',
    'manifests_through': 'Manifests through',
    'tensions_with': 'Tensions with',
    'orients': 'Orients',
    'protects': 'Protects',
    'filters': 'Filters',
    'is_felt_in': 'Felt in',
    'preconditions': 'Preconditions',
    'manifests_loss_of': 'Manifests loss of',
    'foundation_for': 'Foundation for',
    'crystallizes_into': 'Crystallizes into',
    'emerges_within': 'Emerges within',
    'bridges_to': 'Bridges to',
    'provides_space_for': 'Provides space for',
    'hosts': 'Hosts',
    'is_named_through': 'Named through',
    'provides_evidence_for': 'Provides evidence for',
    'tunes': 'Tunes',
  },
};

// Словарь для переводов частей технических ID (элементы после SOUL)
// Формат: часть_ID → перевод
const idPartTranslations: Record<string, Record<string, string>> = {
  ru: {
    // Типы (префиксы)
    'W.ENV': 'Мир-среда',
    'W.PROC': 'Мир-процесс',
    'W.REL': 'Мир-взаимность',
    'W.MEAN': 'Мир-смысловой',
    'W.THRESH': 'Мир-порог',
    'W.VEC': 'Мир-вектор',
    'FOUND': 'Основание',
    'CTX': 'Контекст',
    'PRE': 'Допуск',
    'S': 'Сфера',
    
    // Конкретные названия
    'TRANSBOUNDARY': 'Трансграничность',
    'NON_DOMINATION': 'Недоминирование',
    'DOPUSK': 'Допуск',
    'COEXIST': 'Совместное существование',
    'PURITY': 'Чистота',
    'CAREFIELD': 'Поле заботы',
    'WATER': 'Вода',
    'LIFESUPPORT': 'Жизнеобеспечение',
    'HEATLIGHT': 'Тепло/свет',
    'ECONOMY': 'Экономика',
    'COLOR': 'Цвет',
    'RHYTHM': 'Ритм',
    'OTHER': 'Иное',
    'COMMONGOOD': 'Общее благо',
    'NON_SPEC': 'Неспекулятивность',
    'AGRI': 'Земледелие',
  },
  en: {
    // Types (prefixes)
    'W.ENV': 'World-Environment',
    'W.PROC': 'World-Process',
    'W.REL': 'World-Relation',
    'W.MEAN': 'World-Meaning',
    'W.THRESH': 'World-Threshold',
    'W.VEC': 'World-Vector',
    'FOUND': 'Foundation',
    'CTX': 'Context',
    'PRE': 'Allowance',
    'S': 'Sphere',
    
    // Specific names
    'TRANSBOUNDARY': 'Transboundary',
    'NON_DOMINATION': 'Non-domination',
    'DOPUSK': 'Allowance',
    'COEXIST': 'Coexistence',
    'PURITY': 'Purity',
    'CAREFIELD': 'Care Field',
    'WATER': 'Water',
    'LIFESUPPORT': 'Life Support',
    'HEATLIGHT': 'Heat/Light',
    'ECONOMY': 'Economy',
    'COLOR': 'Color',
    'RHYTHM': 'Rhythm',
    'OTHER': 'Other',
    'COMMONGOOD': 'Common Good',
    'NON_SPEC': 'Non-speculation',
    'AGRI': 'Agriculture',
  },
};

/**
 * Парсит технический ID и собирает читаемое название по частям
 * @param sphereId - технический ID (например, "SOUL.FOUND.TRANSBOUNDARY")
 * @param lang - язык (по умолчанию 'ru')
 * @param nameRu - читаемое название из JSON (если есть, используется для последней части)
 * @returns читаемое название с префиксом типа
 */
export function parseSphereIdToReadableName(
  sphereId: string, 
  lang: string = 'ru',
  nameRu?: string
): { prefix: string; name: string; fullName: string; missingTranslations: string[] } {
  const missingTranslations: string[] = [];
  const translations = idPartTranslations[lang] || idPartTranslations.ru;
  
  // Убираем префикс SOUL. если есть
  const cleanId = sphereId.replace(/^SOUL\./, '');
  const parts = cleanId.split('.');
  
  if (parts.length === 0) {
    return { prefix: '', name: sphereId, fullName: sphereId, missingTranslations };
  }
  
  // Первая часть - это тип (например, FOUND, W.ENV, W.PROC)
  const typePart = parts[0];
  let prefix = '';
  let name = '';
  
  // Определяем префикс типа
  if (parts.length >= 2 && parts[0] === 'W') {
    // Для формата W.ENV, W.PROC и т.д.
    const typeKey = `${parts[0]}.${parts[1]}`;
    prefix = translations[typeKey] || getSphereTypePrefix(sphereId, lang);
    // Остальные части - это название
    const nameParts = parts.slice(2);
    if (nameParts.length > 0) {
      const nameKey = nameParts.join('_');
      name = translations[nameKey] || nameRu || nameParts.join(' ');
      if (!translations[nameKey] && !nameRu) {
        missingTranslations.push(nameKey);
      }
    }
  } else {
    // Для формата FOUND.TRANSBOUNDARY
    prefix = translations[typePart] || getSphereTypePrefix(sphereId, lang);
    if (!translations[typePart]) {
      missingTranslations.push(typePart);
    }
    
    // Остальные части - это название
    const nameParts = parts.slice(1);
    if (nameParts.length > 0) {
      const nameKey = nameParts.join('_');
      name = translations[nameKey] || nameRu || nameParts.join(' ');
      if (!translations[nameKey] && !nameRu) {
        missingTranslations.push(nameKey);
      }
    }
  }
  
  // Если nameRu предоставлено, используем его (это самый надежный источник)
  if (nameRu && nameRu.trim() !== '') {
    name = nameRu;
  }
  
  // Если имя не найдено, используем последнюю часть ID
  if (!name || name.trim() === '') {
    name = parts[parts.length - 1];
    missingTranslations.push(parts[parts.length - 1]);
  }
  
  // Логируем отсутствующие переводы
  if (missingTranslations.length > 0) {
    console.warn(`Missing translations for sphere ${sphereId}:`, missingTranslations);
  }
  
  // Собираем полное название
  const fullName = prefix ? `${prefix}: ${name}` : name;
  
  return { prefix, name, fullName, missingTranslations };
}

/**
 * Получает текущий язык пользователя
 * В будущем можно определить по региону/браузеру
 */
function getCurrentLanguage(): string {
  // TODO: Определение языка по региону пользователя
  // Пока используем русский по умолчанию
  return 'ru';
}

/**
 * Определяет тип сущности по ID и возвращает префикс (синхронная версия)
 */
export function getSphereTypePrefix(sphereId: string, lang: string = 'ru'): string {
  const prefixes: Record<string, Record<string, string>> = {
    ru: {
      'W.ENV': 'Мир-среда',
      'W.PROC': 'Мир-процесс',
      'W.REL': 'Мир-взаимность',
      'W.MEAN': 'Мир-смысловой',
      'W.THRESH': 'Мир-порог',
      'W.VEC': 'Мир-вектор',
      'S.': 'Сфера',
      'FOUND.': 'Основание',
      'CTX.': 'Контекст',
      'PRE.': 'Допуск',
    },
    en: {
      'W.ENV': 'World-Environment',
      'W.PROC': 'World-Process',
      'W.REL': 'World-Relation',
      'W.MEAN': 'World-Meaning',
      'W.THRESH': 'World-Threshold',
      'W.VEC': 'World-Vector',
      'S.': 'Sphere',
      'FOUND.': 'Foundation',
      'CTX.': 'Context',
      'PRE.': 'Allowance',
    },
  };

  const langPrefixes = prefixes[lang] || prefixes.ru;
  
  // Определяем тип по паттернам в ID
  if (sphereId.includes('.W.ENV.')) return langPrefixes['W.ENV'];
  if (sphereId.includes('.W.PROC.')) return langPrefixes['W.PROC'];
  if (sphereId.includes('.W.REL.')) return langPrefixes['W.REL'];
  if (sphereId.includes('.W.MEAN.')) return langPrefixes['W.MEAN'];
  if (sphereId.includes('.W.THRESH.')) return langPrefixes['W.THRESH'];
  if (sphereId.includes('.W.VEC.')) return langPrefixes['W.VEC'];
  if (sphereId.includes('.S.')) return langPrefixes['S.'];
  if (sphereId.includes('.FOUND.')) return langPrefixes['FOUND.'];
  if (sphereId.includes('.CTX.')) return langPrefixes['CTX.'];
  if (sphereId.includes('.PRE.')) return langPrefixes['PRE.'];
  
  return '';
}

/**
 * Определяет тип сущности по ID и возвращает префикс
 * Может работать асинхронно, если нужно загрузить данные сферы
 */
export async function getSphereTypePrefixAsync(sphereId: string, lang: string = 'ru'): Promise<string> {
  // Сначала пробуем определить по паттерну в ID
  const prefix = getSphereTypePrefix(sphereId, lang);
  if (prefix) return prefix;
  
  // Если не удалось определить по паттерну, пытаемся загрузить данные сферы
  try {
    const url = isStatic
      ? `${API_MIRROR}/spheres/${encodeURIComponent(sphereId)}.json`
      : `${API_MIRROR}/sphere/${sphereId}`;
    const response = await fetch(url);
    if (response.ok) {
      const sphereData = await response.json();
      // Используем ID из данных, если он есть
      if (sphereData.id) {
        return getSphereTypePrefix(sphereData.id, lang);
      }
    }
  } catch (error) {
    console.warn(`Failed to load sphere data for prefix detection: ${sphereId}`, error);
  }
  
  return '';
}

/**
 * Форматирует название с префиксом типа
 */
export function formatSphereName(name: string, prefix: string, lang: string = 'ru'): string {
  if (!prefix) return name;
  
  // Если название уже содержит префикс, не добавляем его повторно
  // Проверяем разные варианты префиксов
  const prefixVariants = [
    prefix,
    prefix.replace(':', ''),
    prefix.split(':')[0].trim(),
  ];
  
  for (const variant of prefixVariants) {
    if (name.startsWith(variant)) {
      return name; // Уже есть префикс
    }
  }
  
  const formats: Record<string, string> = {
    ru: `${prefix}: ${name}`,
    en: `${prefix}: ${name}`,
  };
  
  return formats[lang] || formats.ru;
}

/**
 * Транслирует relation kind в читаемое название
 */
export function translateRelationKind(kind: string, lang?: string): string {
  const currentLang = lang || getCurrentLanguage();
  const translations = relationKindTranslations[currentLang] || relationKindTranslations.ru;
  return translations[kind] || kind;
}

/**
 * Загружает название сферы из API и кэширует его
 */
async function loadSphereName(sphereId: string): Promise<string> {
  // Проверяем кэш
  if (sphereNameCache.has(sphereId)) {
    return sphereNameCache.get(sphereId)!;
  }

  try {
    const url = isStatic
      ? `${API_MIRROR}/spheres/${encodeURIComponent(sphereId)}.json`
      : `${API_MIRROR}/sphere/${sphereId}`;
    const response = await fetch(url);
    
    if (!response.ok) {
      // Если не удалось загрузить, возвращаем техническое название
      return sphereId;
    }

    const sphereData = await response.json();
    // Используем name_ru из корня, или из первого элемента, или технический ID
    const baseName = sphereData.name_ru || 
                     (sphereData.elements && sphereData.elements.length > 0 && sphereData.elements[0].text) ||
                     sphereId;
    
    // Добавляем префикс типа
    const currentLang = getCurrentLanguage();
    const prefix = getSphereTypePrefix(sphereId, currentLang);
    const name = formatSphereName(baseName, prefix, currentLang);
    
    // Кэшируем результат
    sphereNameCache.set(sphereId, name);
    return name;
  } catch (error) {
    console.warn(`Failed to load sphere name for ${sphereId}:`, error);
    return sphereId;
  }
}

/**
 * Транслирует технический ID сферы в читаемое название
 * Использует кэш для оптимизации
 */
export async function translateSphereId(sphereId: string, _lang?: string): Promise<string> {
  // Если это не технический ID (уже читаемое название), возвращаем как есть
  if (!sphereId.includes('SOUL.') && !sphereId.includes('.')) {
    return sphereId;
  }

  return loadSphereName(sphereId);
}

/**
 * Синхронная версия трансляции (использует кэш)
 * Если название еще не загружено, возвращает технический ID
 */
export function translateSphereIdSync(sphereId: string): string {
  // Если это не технический ID, возвращаем как есть
  if (!sphereId.includes('SOUL.') && !sphereId.includes('.')) {
    return sphereId;
  }

  return sphereNameCache.get(sphereId) || sphereId;
}

/**
 * Предзагружает названия сфер для списка ID
 * Полезно для предварительной загрузки перед отображением
 */
export async function preloadSphereNames(sphereIds: string[]): Promise<void> {
  await Promise.all(sphereIds.map(id => loadSphereName(id)));
}

/**
 * Очищает кэш (полезно при смене языка)
 */
export function clearCache(): void {
  sphereNameCache.clear();
}

/**
 * Нормализует технический ID из разных форматов в стандартный формат SOUL.*
 */
function normalizeTechnicalId(text: string): string | null {
  // Если уже в формате SOUL.*, возвращаем как есть
  if (text.includes('SOUL.')) {
    return text;
  }
  
  // Убираем лишние символы
  const clean = text.trim().replace(/\s+X\s*$/, '').trim();
  
  // Если формат "W PROC LIFESUPPORT" или "W REL ECONOMY" (3+ слова через пробелы), преобразуем в "SOUL.W.PROC.LIFESUPPORT"
  const words = clean.split(/\s+/);
  if (words.length >= 3 && ['W', 'S', 'FOUND', 'CTX', 'PRE'].includes(words[0])) {
    // Объединяем все слова через точки
    return `SOUL.${words.join('.')}`;
  }
  
  // Если формат "W.REL.ECONOMY" (с точками), добавляем SOUL. префикс
  if (clean.match(/^[A-Z]+\.[A-Z]+\.[A-Z]+/)) {
    const parts = clean.split('.');
    if (['W', 'S', 'FOUND', 'CTX', 'PRE'].includes(parts[0])) {
      return `SOUL.${clean}`;
    }
  }
  
  return null;
}

/**
 * Проверяет, является ли строка техническим ID сферы
 */
export function isTechnicalId(text: string): boolean {
  if (!text || text.trim() === '') return false;
  
  // Проверяем на SOUL. префикс
  if (text.includes('SOUL.')) return true;
  
  // Проверяем на relation kind
  const currentLang = getCurrentLanguage();
  const translations = relationKindTranslations[currentLang] || relationKindTranslations.ru;
  if (translations.hasOwnProperty(text)) return true;
  
  // Проверяем на паттерны технических ID (например, W.REL.ECONOMY)
  if (text.match(/^[A-Z]+\.[A-Z]+\.[A-Z]+/)) return true;
  
  // Проверяем на паттерны типа W MEAN COLOR или W REL ECONOMY (без точек, но заглавные)
  if (text.match(/^[A-Z]+\s+[A-Z]+\s+[A-Z]+/)) return true;
  
  // Проверяем через нормализацию
  const normalized = normalizeTechnicalId(text);
  if (normalized) return true;
  
  return false;
}

/**
 * Транслирует текст элемента сферы (может быть как ID, так и relation kind)
 */
export async function translateElementText(text: string, lang?: string): Promise<string> {
  if (!text || text.trim() === '') return text;
  
  // Убираем лишние символы вроде "X" в конце
  const cleanText = text.replace(/\s+X\s*$/, '').trim();
  
  const currentLang = lang || getCurrentLanguage();
  const translations = relationKindTranslations[currentLang] || relationKindTranslations.ru;
  
  // Если это relation kind, транслируем его
  if (translations[cleanText]) {
    return translations[cleanText];
  }
  
  // Пытаемся нормализовать технический ID
  const normalizedId = normalizeTechnicalId(cleanText);
  if (normalizedId) {
    const translated = await translateSphereId(normalizedId, lang);
    return translated;
  }
  
  // Если это технический ID сферы в другом формате, загружаем название
  if (isTechnicalId(cleanText)) {
    const translated = await translateSphereId(cleanText, lang);
    return translated;
  }
  
  // Иначе возвращаем как есть (уже читаемое название)
  return cleanText;
}

/**
 * Синхронная версия трансляции текста элемента
 */
export function translateElementTextSync(text: string, lang?: string): string {
  if (!text || text.trim() === '') return text;
  
  // Если это relation kind, транслируем его
  const currentLang = lang || getCurrentLanguage();
  const translations = relationKindTranslations[currentLang] || relationKindTranslations.ru;
  if (translations[text]) {
    return translations[text];
  }

  // Если это технический ID сферы, проверяем кэш
  if (isTechnicalId(text)) {
    return translateSphereIdSync(text);
  }

  // Иначе возвращаем как есть
  return text;
}
